import React, { FC, useRef, useEffect, useState, ChangeEvent } from 'react'
import { Box, Button, TextField, Typography, IconButton } from '@mui/material'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import DeleteIcon from '@mui/icons-material/Delete'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from '../contexts/SnackbarContext'
import { useGetImageBlob } from '../hooks/useGetImageBlob'

type Props = {
  title: string
  formData: {
    name: string
    description: string
    address: string
    tags: string
    imageUrl: string
  }
  onFormDataChange: (field: string, value: string) => void
  onSubmit: (imageFile: File | null) => Promise<void>
  existingImageUrl?: string // 編集時の既存画像URL
  isEditMode?: boolean // 編集モードかどうか
}

export const ShopFormLayout: FC<Props> = ({
  title,
  formData,
  onFormDataChange,
  onSubmit,
  existingImageUrl,
  isEditMode = false,
}) => {
  const navigate = useNavigate()
  const { showSnackbar } = useSnackbar()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [isImageProcessing, setIsImageProcessing] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [imageRemoved, setImageRemoved] = useState(false) // 画像が削除されたかどうか

  const { imageBlob } = useGetImageBlob({ file: imageFile }, setIsImageProcessing)

  // existingImageUrl を ref で追跡（useEffect の deps から外すため）
  const existingImageUrlRef = useRef(existingImageUrl)
  useEffect(() => {
    existingImageUrlRef.current = existingImageUrl
  })

  // 表示する画像URLを決定
  const getDisplayImageUrl = () => {
    // 新しい画像が選択されている場合
    if (formData.imageUrl && !imageRemoved) {
      return formData.imageUrl
    }
    // 編集モードで既存画像があり、削除されていない場合
    if (isEditMode && existingImageUrl && !imageRemoved) {
      return existingImageUrl
    }
    return ''
  }

  useEffect(() => {
    if (!imageBlob) {
      // 編集モードで新しい画像が選択されていない場合は、既存画像URLを保持
      if (isEditMode && !imageRemoved) {
        onFormDataChange('imageUrl', existingImageUrlRef.current || '')
      } else {
        onFormDataChange('imageUrl', '')
      }
      return
    }

    const previewUrl = URL.createObjectURL(imageBlob)
    onFormDataChange('imageUrl', previewUrl)
    setImageRemoved(false) // 新しい画像が選択されたら削除フラグをリセット

    return () => {
      URL.revokeObjectURL(previewUrl)
    }
  }, [imageBlob, isEditMode, imageRemoved])

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setImageFile(e.target.files[0])
      setImageRemoved(false)
    }
  }

  const handleCancelImageFile = () => {
    setImageFile(null)
    setImageRemoved(true)
    onFormDataChange('imageUrl', '')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      showSnackbar('お店の名前を入力してください。', 'error')
      return
    }

    setIsSubmitting(true)

    try {
      // 画像が削除された場合はnullを渡す
      let fileToSubmit: File | null = null

      if (!imageRemoved && imageBlob) {
        fileToSubmit =
          imageBlob instanceof File
            ? imageBlob
            : new File([imageBlob], 'converted-image.jpg', { type: 'image/jpeg' })
      }

      await onSubmit(fileToSubmit)

      // 編集モードの場合は前のページに戻る、新規作成の場合はホームに戻る
      if (isEditMode) {
        navigate(-1)
      } else {
        navigate('/')
      }
    } catch (error) {
      showSnackbar('送信エラーが発生しました。もう一度お試しください。', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const displayImageUrl = getDisplayImageUrl()

  return (
    <Box maxWidth="407px" m="auto" p={2}>
      <Button
        variant="text"
        startIcon={<ArrowBackIosNewIcon />}
        onClick={() => navigate(-1)}
        sx={{ marginBottom: '8px' }}
      >
        戻る
      </Button>

      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
        {title}
      </Typography>

      <Box my={2}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          {displayImageUrl ? (
            <Box
              sx={{
                width: '100%',
                maxWidth: 375,
                height: 'auto',
                aspectRatio: '4/5',
                position: 'relative',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <img
                src={displayImageUrl}
                alt="アップロードプレビュー"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0,
                  transition: 'opacity 0.2s',
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 1,
                  },
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>
                  画像を変更
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={handleCancelImageFile}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  background: 'rgba(255, 255, 255, 0.9)',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 1)',
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <Box
              sx={{
                width: '100%',
                maxWidth: 375,
                height: 'auto',
                aspectRatio: '4/5',
                border: '2px dashed',
                borderColor: 'grey.300',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
                background: 'grey.50',
                '&:hover': {
                  borderColor: 'primary.main',
                  background: 'grey.100',
                },
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                画像をアップロード
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                クリックして画像を選択
              </Typography>
            </Box>
          )}
          <input
            type="file"
            accept="image/*"
            hidden
            ref={fileInputRef}
            onChange={handleFileChange}
          />
        </Box>
        {isImageProcessing && (
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', mt: 1, display: 'block', textAlign: 'center' }}
          >
            画像処理中...
          </Typography>
        )}
      </Box>

      <TextField
        label="お店の名前"
        value={formData.name}
        onChange={(e) => onFormDataChange('name', e.target.value)}
        fullWidth
        margin="normal"
        required
        placeholder="例）タンバム"
      />
      <TextField
        label="メモ"
        value={formData.description}
        onChange={(e) => onFormDataChange('description', e.target.value)}
        fullWidth
        margin="normal"
        multiline
        rows={6}
      />
      <TextField
        label="場所"
        value={formData.address}
        onChange={(e) => onFormDataChange('address', e.target.value)}
        fullWidth
        margin="normal"
        placeholder="例）地名やリンクなど"
      />
      <TextField
        label="タグ"
        value={formData.tags}
        onChange={(e) => onFormDataChange('tags', e.target.value)}
        fullWidth
        margin="normal"
        placeholder="例）六本木、韓国料理"
      />

      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        sx={{ mt: 2, py: 2, fontWeight: 'bold' }}
        fullWidth
        loading={isSubmitting}
        disabled={isImageProcessing}
      >
        {isEditMode ? '更新する' : '追加する'}
      </Button>
    </Box>
  )
}
